/**
 * Maps from OpenStreetMap, as Funely does: Leaflet draws OpenStreetMap tiles, and Photon (komoot's free
 * OpenStreetMap search: no key, no account; fair use) finds addresses. Nothing to configure at build time.
 */
export const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const PHOTON_URL = 'https://photon.komoot.io';

/** Where the map opens when the business has no location yet: OlaCore's own default. */
export const DEFAULT_CENTER = { latitude: 51.50722, longitude: -0.1275 };

/** Unset is missing, blank or 0,0, which is what a new organisation form starts with. */
export const hasLocation = (latitude, longitude) => {
  if (latitude === '' || longitude === '' || latitude == null || longitude == null) return false;
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

/** Six decimals is about 10 cm, finer than anyone can place a pin. */
export const roundCoordinate = (value) => Math.round(value * 1e6) / 1e6;

/** OpenStreetMap's embeddable map around a point, with a marker on it. */
export const embedUrl = (latitude, longitude, span = 0.004) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const bbox = [lng - span, lat - span / 2, lng + span, lat + span / 2].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`;
};

/** OpenStreetMap directions to a point; the start is left for the visitor's own location. */
export const directionsUrl = (latitude, longitude) =>
  `https://www.openstreetmap.org/directions?route=${encodeURIComponent(`;${latitude},${longitude}`)}`;

/** One line from a Photon result's parts, leaving out what is missing or repeated. */
export const placeFromFeature = (feature) => {
  const coords = feature?.geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const p = feature.properties || {};
  const street = [p.housenumber, p.street].filter(Boolean).join(' ');
  const town = [p.postcode, p.city || p.town || p.village || p.district].filter(Boolean).join(' ');
  const parts = [];
  [p.name, street, town, p.country].forEach((part) => {
    if (part && !parts.includes(part)) parts.push(part);
  });
  return {
    latitude: roundCoordinate(coords[1]),
    longitude: roundCoordinate(coords[0]),
    label: parts.join(', '),
  };
};

/** Places matching what was typed, best first. */
export const searchPlaces = async (query, signal) => {
  const response = await fetch(`${PHOTON_URL}/api/?q=${encodeURIComponent(query)}&limit=5&lang=en`, { signal });
  if (!response.ok) throw new Error('The address search is not answering right now. Try again in a moment.');
  const body = await response.json();
  return (body.features || []).map(placeFromFeature).filter(Boolean);
};

const NOT_FOUND = 'That address could not be found. Try adding the town or postcode.';

/** The best match for an address; rejects with a message a business owner can act on. */
export const geocodeAddress = async (address) => {
  const places = await searchPlaces(address);
  if (places.length === 0) throw new Error(NOT_FOUND);
  return places[0];
};

/** The browser's position, for "Use my current location". */
export const currentPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('This browser cannot share its location.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: roundCoordinate(pos.coords.latitude),
        longitude: roundCoordinate(pos.coords.longitude),
      }),
      () => reject(new Error('Your location could not be read. Allow location access, or search for the address.')),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
